import React, { useState, useEffect } from "react";
import { getPatientMatches, deletePatientMatch } from "../APIs/patientService";
import { addFavorite, removeFavorite } from "../APIs/favoriteApi";
import { useNavigate } from "react-router-dom";

// Small wrapper that turns the card into a clickable target
function ProviderMatchCard({ provider, patientId, children }) {
  const navigate = useNavigate();

  // Matches your ProviderProfilePage params: /provider/:id/:patientId? (falls back to /provider/:id)
  //const to = patientId ? `/provider/${provider.id}/${patientId}` : `/provider/${provider.id}`;
  const to = patientId
    ? `/provider/${provider.id}/patient/${patientId}`
    : `/provider/${provider.id}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate(to)}
      className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:ring-2 hover:ring-blue-200 cursor-pointer transition"
    >
      {children}
    </div>
  );
}

export default function ProviderMatchesTab({ patientId }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(""); // ✅ For temporary messages
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const matchData = await getPatientMatches(patientId);
        const enrichedMatches = matchData.map((provider) => ({
          id: provider.providerId,
          name: `${provider.firstName} ${provider.lastName}`,
          email: provider.email || "",
          specialty: provider.specialties || "",
          location: `${provider.city}, ${provider.state}`,
          matchScore: provider.matchScore || 0,
          isFavorite: provider.isFavorite || false,
        }));
        setProviders(enrichedMatches);
      } catch (err) {
        console.error("Error fetching matches:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [patientId]);

  const toggleFavorite = async (id, isFavorite) => {
    try {
      if (isFavorite) {
        await removeFavorite(patientId, id);
      } else {
        await addFavorite(patientId, id);
      }
      setProviders((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, isFavorite: !p.isFavorite } : p,
        ),
      );
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  };

  const handleDeleteMatch = async (providerId) => {
    try {
      await deletePatientMatch(patientId, providerId);
      setProviders((prev) => prev.filter((m) => m.id !== providerId));

      // ✅ Show temporary toast
      setToast("Match deleted successfully!");
      setTimeout(() => setToast(""), 3000); // Hide after 3 seconds
    } catch (err) {
      console.error("Error deleting match:", err);
      setToast("Error deleting match");
      setTimeout(() => setToast(""), 3000);
    }
  };

  if (loading) return <p>Loading matches...</p>;

  return (
    <div>
      <h3 className="text-xl font-semibold mb-4">Provider Matches</h3>

      {toast && (
        <div className="mb-4 p-2 bg-green-100 text-green-800 rounded">
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {providers.map((provider) => (
          <ProviderMatchCard
            key={provider.id}
            provider={provider}
            patientId={patientId}
          >
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-lg font-bold text-gray-800">
                  {provider.name}
                </h4>
                <p className="text-sm text-gray-600">{provider.specialty}</p>
                <p className="text-sm text-gray-500">{provider.location}</p>
                <p className="text-sm text-blue-700">{provider.email}</p>
              </div>

              <span className="text-sm font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                Match Score: {provider.matchScore}%
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // prevent card click
                  navigate(`/provider/${provider.id}/patient/${patientId}`);
                }}
                className="bg-blue-600 text-white text-sm px-4 py-1 rounded hover:bg-blue-700"
              >
                Book Appointment
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(provider.id, provider.isFavorite);
                }}
                className={`text-sm px-3 py-1 rounded ${
                  provider.isFavorite
                    ? "text-red-600 bg-red-100"
                    : "text-gray-600 hover:text-red-500"
                }`}
              >
                {provider.isFavorite ? "❤️ Favorites" : "☆ Add to Favorites"}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation(); /* open contact flow here */
                }}
                className="text-sm text-green-600 hover:underline"
              >
                📧 Contact
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteMatch(provider.id);
                }}
                className="text-sm text-red-600 hover:underline"
              >
                🗑 Delete Match
              </button>
            </div>
          </ProviderMatchCard>
        ))}
      </div>
    </div>
  );
}
